import { createSlice, PayloadAction, Dispatch } from "@reduxjs/toolkit";
import { Bot, BotCall, BotEmotion, BotSkill } from "interfaces/db";
import { BotSessionQueryParams } from "interfaces/bots";
import { getServicesManager } from "services";


export interface BotSkillsState {
  skills: BotSkill[] | null;
}

export const botSkillsInitialState: BotSkillsState = {
  skills: null,
};

export const teamBotSkillsSlice = createSlice({
  name: 'teamBotSkills',
  initialState: botSkillsInitialState,
  reducers: {
    updateTeamBotSkills: (state, action: PayloadAction<BotSkill[]>) => {
      state.skills = action.payload;
    },
  }
});

export const globalBotSkillsSlice = createSlice({
  name: 'globalBotSkills',
  initialState: botSkillsInitialState,
  reducers: {
    updateGlobalBotSkills: (state, action: PayloadAction<BotSkill[]>) => {
      state.skills = action.payload;
    }
  }
});

export const { updateTeamBotSkills } = teamBotSkillsSlice.actions;
export const { updateGlobalBotSkills } = globalBotSkillsSlice.actions;

export function reloadTeamBotSkills(dispatch: Dispatch): Promise<BotSkill[] | null> {
  const result = getServicesManager().getTeamBotSkills();
  result.then(v => {
    if (v !== null) {
      dispatch(teamBotSkillsSlice.actions.updateTeamBotSkills(v));
    }
  });
  return result;
}

export function reloadGlobalBotSkills(dispatch: Dispatch): Promise<BotSkill[] | null> {
  const result = getServicesManager().getGlobalBotSkills();
  result.then(v => {
    if (v !== null) {
      dispatch(globalBotSkillsSlice.actions.updateGlobalBotSkills(v));
    }
  });
  return result;
}export interface BotsResult {
  bots: Bot[] | null;
}
const botsInitialState: BotsResult = {
  bots: null,
};

export const teamBotsSlice = createSlice({
  name: 'bots',
  initialState: botsInitialState,
  reducers: {
    updateTeamBots: (state, action: PayloadAction<Bot[]>) => {
      state.bots = action.payload;
    },
    updateSingleBot: (state, action: PayloadAction<Bot>) => {
      if (state.bots) {
        state.bots = state.bots.map(bot => bot.bot_id === action.payload.bot_id ? action.payload : bot
        );
      }
    },
  },
});

export const { updateTeamBots, updateSingleBot } = teamBotsSlice.actions;

export function reloadBots(dispatch: Dispatch): Promise<Bot[] | null> {
  const result = getServicesManager().getAllTeamBots();
  result.then(v => {
    if (v !== null) {
      dispatch(teamBotsSlice.actions.updateTeamBots(v));
    }
  });
  return result;
}

export const globalBotsSlice = createSlice({
  name: 'globalBots',
  initialState: botsInitialState,
  reducers: {
    updateGlobalBots: (state, action: PayloadAction<Bot[]>) => {
      state.bots = action.payload;
    },
    updateSingleGlobalBot: (state, action: PayloadAction<Bot>) => {
      if (state.bots) {
        state.bots = state.bots.map(bot => bot.bot_id === action.payload.bot_id ? action.payload : bot
        );
      }
    },
  },
});

export const { updateGlobalBots, updateSingleGlobalBot } = globalBotsSlice.actions;

export function reloadGlobalBots(dispatch: Dispatch): Promise<Bot[] | null> {
  const result = getServicesManager().getAllGlobalBots();
  result.then((v) => {
    if (v !== null) {
      dispatch(globalBotsSlice.actions.updateGlobalBots(v));
    }
  });
  return result;
}

export interface BotsToEmotion {
  botToEmotions: { [k: string]: BotEmotion[]; };
}
const botsToEmotionInitialState: BotsToEmotion = {
  botToEmotions: {},
};

export const botsToEmotionSlice = createSlice({
  name: 'botsToEmotion',
  initialState: botsToEmotionInitialState,
  reducers: {
    updateBotsToEmotion: (state, action: PayloadAction<{ [k: string]: BotEmotion[]; }>) => {
      state.botToEmotions = { ...state.botToEmotions, ...action.payload };
    },
  },
});

export function getEmotionsForBot(bot_ids: string[], dispatch: Dispatch): Promise<BotEmotion[] | null> {
  const result = getServicesManager().getAllEmotions(bot_ids);
  result.then(v => {
    if (v !== null) {
      const bot_ids = new Set(v.map(e => e.bot_id));
      const update = Object.fromEntries(Array.from(bot_ids).map(bot_id => [bot_id, v.filter(e => e.bot_id === bot_id)]));
      dispatch(botsToEmotionSlice.actions.updateBotsToEmotion(update));
    }
  });
  return result;
}

export const { updateBotsToEmotion } = botsToEmotionSlice.actions;

export function convertQueryToString(query: BotSessionQueryParams): string {
  return JSON.stringify(query);
}

export interface SyntheticSessionqueryData {
  param: BotSessionQueryParams;
}
const syntheticSessionQueryDataInitialState: SyntheticSessionqueryData = {
  param: {}
};

export const syntheticSessionQueryDataSlice = createSlice({
  name: 'syntheticSessionQueryData',
  initialState: syntheticSessionQueryDataInitialState,
  reducers: {
    updateSyntheticQueryParam: (state, action: PayloadAction<BotSessionQueryParams>) => {
      state.param = action.payload;
    },
  },
});

export const { updateSyntheticQueryParam } = syntheticSessionQueryDataSlice.actions;

export interface SyntheticConversationalData {
  data: { [k: string]: BotCall[]; };
  hasMore: { [k: string]: boolean; };
}
const syntheticConversationalDataInitialState: SyntheticConversationalData = {
  data: {},
  hasMore: {}
};
const CALLS_PER_PAGE = 50;

export const syntheticConversationalDataSlice = createSlice({
  name: 'syntheticConversationalData',
  initialState: syntheticConversationalDataInitialState,
  reducers: {
    updateConversationData: (state, action: PayloadAction<{
      queryKey: string;
      calls: BotCall[];
      isBaseQuery: boolean;
    }>) => {
      const { queryKey, calls, isBaseQuery } = action.payload;
      const existing_data = (state.data[queryKey] ?? []);
      const existing_ids = existing_data.map((v) => v.bot_call_id);
      const new_data = calls.filter((v) => !existing_ids.includes(v.bot_call_id));
      // override the existing data with the new data
      const existing_data_with_new_data = existing_data.map((v) => {
        const new_call = calls.find((new_v) => new_v.bot_call_id === v.bot_call_id);
        return new_call ?? v;
      });

      // Combine and sort all calls by start date in descending order
      state.data[queryKey] = [...existing_data_with_new_data, ...new_data].sort((a, b) => new Date(b.start).getTime() - new Date(a.start).getTime()
      );

      // Only update hasMore for the base query
      if (isBaseQuery) {
        state.hasMore[queryKey] = calls.length === CALLS_PER_PAGE;
      }
    },
  },
});

export const { updateConversationData } = syntheticConversationalDataSlice.actions;

export interface AllSyntheticConversationalData {
  data: BotCall[];
}
const allSyntheticConversationalDataInitialState: AllSyntheticConversationalData = {
  data: []
};

export const allSyntheticConversationalDataSlice = createSlice({
  name: 'allSyntheticConversationalData',
  initialState: allSyntheticConversationalDataInitialState,
  reducers: {
    updateAllConversationData: (state, action: PayloadAction<BotCall[]>) => {
      const current_ids = state.data.map((v) => v.bot_call_id);
      const new_data = action.payload.filter((v) => !current_ids.includes(v.bot_call_id));
      // override the existing data with the new data
      const existing_data_with_new_data = state.data.map((v) => {
        const new_call = action.payload.find((new_v) => new_v.bot_call_id === v.bot_call_id);
        return new_call ?? v;
      });
      state.data = [...existing_data_with_new_data, ...new_data];
    },
  },
});

export const { updateAllConversationData } = allSyntheticConversationalDataSlice.actions;

export function getSyntheticConversationalData(
  query: BotSessionQueryParams,
  dispatch: Dispatch,
  lastCallTime?: Date
): Promise<BotCall[] | null> {
  const baseQueryKey = convertQueryToString(query);

  const queryWithEnd = lastCallTime ? {
    ...query,
    end: lastCallTime
  } : query;
  const result = getServicesManager().getSynthCalls(queryWithEnd);
  result.then(v => {
    if (v !== null) {
      dispatch(syntheticConversationalDataSlice.actions.updateConversationData({
        queryKey: baseQueryKey,
        calls: v,
        isBaseQuery: !lastCallTime
      }));
      dispatch(allSyntheticConversationalDataSlice.actions.updateAllConversationData(v));
    }
  });
  return result;
}
// Add new slice for active call

export const activeSynthCallSlice = createSlice({
  name: 'activeSynthCall',
  initialState: {
    call: null as BotCall | null
  },
  reducers: {
    setActiveSynthCall: (state, action: PayloadAction<BotCall | null>) => {
      state.call = action.payload;
    }
  }
});

export const { setActiveSynthCall } = activeSynthCallSlice.actions;
// Add new interface and slice for call analysis

export interface CallAnalysisState {
  pending: string[];
  data: { [callId: string]: string; };
}
const callAnalysisInitialState: CallAnalysisState = {
  pending: [],
  data: {},
};

export const callAnalysisSlice = createSlice({
  name: 'callAnalysis',
  initialState: callAnalysisInitialState,
  reducers: {
    setAnalysisPending: (state, action: PayloadAction<string>) => {
      if (!state.pending.includes(action.payload)) {
        state.pending = state.pending.concat(action.payload);
      }
    },
    setAnalysisComplete: (state, action: PayloadAction<{ callId: string; analysis: string; }>) => {
      state.pending = state.pending.filter(id => id !== action.payload.callId);
      state.data[action.payload.callId] = action.payload.analysis;
    },
  }
});

export const { setAnalysisPending, setAnalysisComplete } = callAnalysisSlice.actions;
// Add to your store configuration
// Add this new function to load calls for a specific bot

export function getBotSpecificCalls(
  botId: string,
  dispatch: Dispatch,
  lastCallTime?: Date
): Promise<BotCall[] | null> {
  // Create a bot-specific query
  const botSpecificQuery: BotSessionQueryParams = {
    bot_ids: [botId],
  };

  if (lastCallTime) {
    botSpecificQuery.end = lastCallTime;
  }

  const result = getServicesManager().getSynthCalls(botSpecificQuery);
  result.then(v => {
    if (v !== null) {
      dispatch(syntheticConversationalDataSlice.actions.updateConversationData({
        queryKey: convertQueryToString(botSpecificQuery),
        calls: v,
        isBaseQuery: !lastCallTime,
      }));
      dispatch(allSyntheticConversationalDataSlice.actions.updateAllConversationData(v));
    }
  });
  return result;
}

